import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { API, fmtDateTime } from "@/api";
import { TransferPlanForm } from "@/components/TransferPlanForm";
import { PlanStatusBadge } from "@/pages/TransferPlans";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ctnText = (l, pcs) => {
  const ppc = l.pcs_per_ctn || 0;
  if (ppc <= 0 || pcs <= 0) return "";
  const c = Math.floor(pcs / ppc);
  const rem = Math.round((pcs % ppc) * 100) / 100;
  if (c === 0) return "";
  return rem ? `${c} ctn + ${rem} ${l.unit}` : `${c} ctn`;
};

const STATUS_LABEL = { confirmed: "Confirmed as planned", adjusted: "Adjusted", skipped: "Not loaded" };
const STATUS_STYLE = {
  confirmed: "bg-emerald-100 text-emerald-700",
  adjusted: "bg-indigo-100 text-indigo-700",
  skipped: "bg-slate-200 text-slate-600",
};

export default function TransferPlanDetail() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [adjustIdx, setAdjustIdx] = useState(null);
  const [adjCtn, setAdjCtn] = useState("");
  const [adjPcs, setAdjPcs] = useState("");
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, wh, pr] = await Promise.all([
        API.get(`/transfer-plans/${id}`),
        API.get("/warehouses"),
        API.get("/products"),
      ]);
      setPlan(p.data);
      setWarehouses(wh.data);
      setProducts(pr.data.filter((x) => (x.type || "product") !== "service"));
      const n = {};
      p.data.lines.forEach((l, i) => {
        n[i] = l.note || "";
      });
      setNotes(n);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (idx, resolution, extra = {}) => {
    if (busy) return;
    setBusy(true);
    try {
      await API.patch(`/transfer-plans/${id}/lines/${idx}`, {
        resolution,
        qty_ctn: Number(extra.qty_ctn) || 0,
        qty_pcs: Number(extra.qty_pcs) || 0,
        note: notes[idx] || "",
      });
      setAdjustIdx(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update line");
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await API.post(`/transfer-plans/${id}/complete`);
      toast.success("Transfer completed — stock moved between warehouses");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete plan");
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="space-y-4" data-testid="transfer-plan-not-found">
        <Button variant="outline" size="sm" asChild>
          <Link to="/transfer-plans"><ArrowLeft className="mr-1 h-4 w-4" /> Back to Transfer Plans</Link>
        </Button>
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-sm text-slate-500">Plan not found.</div>
      </div>
    );
  }
  if (!plan) return <div className="text-sm text-slate-500" data-testid="transfer-plan-loading">Loading plan...</div>;

  const isPlanned = plan.status === "planned";
  const allPending = plan.lines.every((l) => l.resolution === "pending");
  const resolvedCount = plan.lines.filter((l) => l.resolution !== "pending").length;
  const allResolved = resolvedCount === plan.lines.length;

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-24" data-testid="transfer-plan-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" asChild data-testid="transfer-plan-back">
          <Link to="/transfer-plans"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="flex flex-wrap items-center gap-2 font-heading text-xl font-bold text-slate-900 sm:text-2xl">
            <span className="truncate">{plan.source_warehouse_name}</span>
            <ArrowRight className="h-5 w-5 shrink-0 text-indigo-500" />
            <span className="truncate">{plan.dest_warehouse_name}</span>
          </h1>
          <p className="text-xs text-slate-500">
            {plan.created_by_name} · {fmtDateTime(plan.created_at)}
            {plan.completed_at ? ` · Completed by ${plan.completed_by} · ${fmtDateTime(plan.completed_at)}` : ""}
          </p>
        </div>
        <PlanStatusBadge status={plan.status} testid="transfer-plan-status" />
      </div>

      {plan.note && <div className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600" data-testid="transfer-plan-note">{plan.note}</div>}

      {isPlanned && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2.5" data-testid="plan-progress">
          <span className="text-sm font-medium text-indigo-800">{resolvedCount} of {plan.lines.length} items resolved</span>
          {allPending && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="plan-edit-button">
              <Pencil className="mr-1 h-3.5 w-3.5" /> Edit Plan
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3" data-testid="transfer-plan-lines">
        {plan.lines.map((l, i) => {
          const plannedCtn = ctnText(l, l.planned_qty);
          const confirmedCtn = ctnText(l, l.confirmed_qty || 0);
          return (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`line-card-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{l.product_name}</div>
                  <div className="mt-0.5 text-xs text-slate-500" data-testid={`line-planned-${i}`}>
                    Planned: {l.planned_qty} {l.unit}{plannedCtn ? ` (${plannedCtn})` : ""}
                  </div>
                </div>
                {l.resolution !== "pending" && (
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLE[l.resolution]}`} data-testid={`line-status-${i}`}>
                    {STATUS_LABEL[l.resolution]}{l.resolution !== "skipped" ? ` — ${l.confirmed_qty} ${l.unit}` : ""}
                  </span>
                )}
              </div>

              {l.resolution === "adjusted" && (
                <div className="mt-2 text-xs font-medium text-indigo-700" data-testid={`line-adjusted-info-${i}`}>
                  {l.planned_qty} → {l.confirmed_qty} {l.unit}{confirmedCtn ? ` (${confirmedCtn})` : ""}
                </div>
              )}
              {l.note && <div className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800" data-testid={`line-note-display-${i}`}>{l.note}</div>}

              {isPlanned && (
                <>
                  <Input
                    value={notes[i] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [i]: e.target.value }))}
                    placeholder="Note (optional) — e.g. no space in truck"
                    className="mt-3 h-9 text-xs"
                    data-testid={`line-note-input-${i}`}
                  />
                  {adjustIdx === i ? (
                    <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/50 p-3" data-testid={`line-adjust-panel-${i}`}>
                      <div className={`grid gap-2 ${(l.pcs_per_ctn || 0) > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                        {(l.pcs_per_ctn || 0) > 0 && (
                          <div>
                            <label className="text-xs font-medium text-slate-600">Cartons (1 ctn = {l.pcs_per_ctn} pcs)</label>
                            <Input type="number" min="0" value={adjCtn} onChange={(e) => setAdjCtn(e.target.value)} className="mt-1" data-testid={`line-adj-ctn-${i}`} />
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-slate-600">{l.unit}</label>
                          <Input type="number" min="0" value={adjPcs} onChange={(e) => setAdjPcs(e.target.value)} className="mt-1" data-testid={`line-adj-pcs-${i}`} />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => resolve(i, "adjusted", { qty_ctn: adjCtn, qty_pcs: adjPcs })} disabled={busy} data-testid={`line-adj-save-${i}`}>
                          Save Adjusted Qty
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setAdjustIdx(null)} data-testid={`line-adj-cancel-${i}`}>Cancel</Button>
                        {(l.pcs_per_ctn || 0) > 0 && (
                          <span className="ml-auto text-[11px] font-medium text-indigo-600" data-testid={`line-adj-total-${i}`}>
                            = {(Number(adjCtn) || 0) * (l.pcs_per_ctn || 0) + (Number(adjPcs) || 0)} {l.unit}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Button className="h-11 bg-emerald-600 text-xs hover:bg-emerald-700 sm:text-sm" onClick={() => resolve(i, "confirmed")} disabled={busy} data-testid={`line-confirm-${i}`}>
                        <Check className="mr-1 h-4 w-4" /> Confirm
                      </Button>
                      <Button variant="outline" className="h-11 text-xs sm:text-sm" onClick={() => { setAdjustIdx(i); setAdjCtn(""); setAdjPcs(""); }} disabled={busy} data-testid={`line-adjust-${i}`}>
                        <Pencil className="mr-1 h-4 w-4" /> Adjust
                      </Button>
                      <Button variant="outline" className="h-11 text-xs text-rose-600 hover:text-rose-700 sm:text-sm" onClick={() => resolve(i, "skipped")} disabled={busy} data-testid={`line-skip-${i}`}>
                        <X className="mr-1 h-4 w-4" /> Not loaded
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {isPlanned && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 p-3 backdrop-blur-md lg:pl-64" data-testid="complete-bar">
          <div className="mx-auto max-w-2xl">
            <Button
              className="h-12 w-full bg-indigo-600 text-base font-semibold hover:bg-indigo-700 disabled:opacity-50"
              onClick={complete}
              disabled={!allResolved || busy}
              data-testid="complete-plan-button"
            >
              {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
              {allResolved ? "Complete Transfer — Move Stock" : `Resolve ${plan.lines.length - resolvedCount} more item${plan.lines.length - resolvedCount === 1 ? "" : "s"} to complete`}
            </Button>
          </div>
        </div>
      )}

      {!isPlanned && (
        <div className="rounded-lg border border-slate-200 bg-white" data-testid="planned-actual-table">
          <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Planned vs Actual</div>
          <div className="divide-y divide-slate-100">
            {plan.lines.map((l, i) => {
              const delta = Math.round(((l.confirmed_qty || 0) - l.planned_qty) * 100) / 100;
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`planned-actual-row-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{l.product_name}</div>
                    {l.note && <div className="mt-0.5 text-xs text-amber-700">{l.note}</div>}
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>Planned {l.planned_qty}</div>
                    <div className="font-semibold text-slate-800" data-testid={`planned-actual-qty-${i}`}>Actual {l.confirmed_qty ?? 0}</div>
                  </div>
                  <span className={`w-16 text-right text-sm font-bold ${delta === 0 ? "text-emerald-600" : delta < 0 ? "text-amber-600" : "text-indigo-600"}`} data-testid={`planned-actual-delta-${i}`}>
                    {delta === 0 ? "—" : delta > 0 ? `+${delta}` : delta}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <TransferPlanForm open={editOpen} onOpenChange={setEditOpen} warehouses={warehouses} products={products} plan={plan} onSaved={load} />
    </div>
  );
}
